import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api/client";
import { Avatar } from "@/components/Avatar";
import { Panel, Quiet } from "@/components/home/panel";
import { RowIcon } from "@/components/home/tokens";
import { BADGE_META, type Badge, type HistoryRow } from "@/components/Leaderboard";
import { TierChip, TierProgress, type TierStep } from "@/components/tiers";

/** Mirrors `app/schemas/leaderboard.py`. */
interface BoardRow {
  rank: number;
  user_id: string;
  full_name: string | null;
  email: string;
  score: number;
  listings: number;
  deals_done: number;
  tier: string;
  is_me: boolean;
}
interface MyStanding {
  rank: number;
  of: number;
  score: number;
  behind_next: number | null;
  next_rank: number | null;
  streak: number;
  tier: string;
  next_tier: string | null;
  to_next_tier: number | null;
}
interface LeaderboardResponse {
  period_label: string;
  leaders: BoardRow[];
  me: MyStanding | null;
  my_badges: Badge[];
  my_history: HistoryRow[];
  tiers: TierStep[];
}

const MEDAL = ["🥇", "🥈", "🥉"];

/** Compact standings for Home: the podium, then your own row and the gap.
 *
 *  Fixed height by construction — three podium rows plus at most one self row —
 *  so it cannot push Home past the one-screen budget however the month goes.
 *
 *  Fetched as a second wave rather than folded into `/api/home`: the board is
 *  not on the critical path for starting the day, and ProducerHome already uses
 *  this pattern for the market rails.
 */
export function RankPanel() {
  const [data, setData] = useState<LeaderboardResponse | null>(null);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    let alive = true;
    api<LeaderboardResponse>("/api/leaderboard")
      .then((r) => alive && setData(r))
      // A 403 means the agency switched the board off for this role. Home must
      // never error, so the block simply disappears.
      .catch(() => alive && setDenied(true));
    return () => {
      alive = false;
    };
  }, []);

  if (denied) return null;
  if (!data) return <Panel title="Standings"><Quiet title="Loading…" /></Panel>;

  const { leaders, me } = data;
  // Suppress the self row only when it is already one of the leaders above.
  const listed = me != null && leaders.some((r) => r.is_me);
  const counts = new Map<string, number>();
  for (const b of data.my_badges ?? []) counts.set(b.kind, (counts.get(b.kind) ?? 0) + 1);
  const badgeKinds = [...counts.entries()];
  // One line of history is enough on Home: the best finish is the thing worth
  // beating. The month-by-month record lives on the board.
  // Only months you actually scored in: ranks tie at zero, so a month where
  // the whole agency published nothing would advertise a bogus "Best · #2".
  const best = (data.my_history ?? [])
    .filter((r) => r.score > 0)
    .reduce<HistoryRow | null>((b, r) => (b == null || r.rank < b.rank ? r : b), null);

  return (
    <Panel
      title={`${data.period_label} standings`}
      right={
        me && me.streak > 1 ? (
          <span className="flex items-center gap-1 text-amber-300">
            <RowIcon name="flame" size={12} />
            {me.streak}
          </span>
        ) : undefined
      }
      flush
    >
      <Link to="/leaderboard" className="block hover:bg-white/[0.02] transition">
        <div className="divide-y divide-white/[0.04]">
          {leaders.map((r, i) => (
            <div
              key={r.user_id}
              className={`flex items-center gap-2.5 px-4 py-1 ${
                r.is_me ? "bg-accent-500/[0.06]" : ""
              }`}
            >
              <span
                className={`w-5 text-center flex-shrink-0 ${
                  i < 3 ? "text-[13px]" : "text-[11px] tabular-nums text-slate-500"
                }`}
              >
                {MEDAL[i] ?? r.rank}
              </span>
              <Avatar name={r.full_name || r.email} size="xs" tier={r.tier} />
              <span className="text-[12.5px] text-slate-200 truncate flex-1 min-w-0">
                {r.is_me ? "You" : r.full_name || r.email}
              </span>
              <TierChip tier={r.tier} bare />
              <span className="text-[12.5px] tabular-nums text-slate-300 flex-shrink-0">
                {r.score.toLocaleString()}
              </span>
            </div>
          ))}
        </div>

        {/* Own row only when off the podium — otherwise it would duplicate. */}
        {me && !listed && (
          <div className="border-t border-white/10 px-4 py-2 bg-accent-500/[0.06]">
            <div className="flex items-center gap-2.5">
              <span className="text-[11px] tabular-nums text-slate-400 w-5 text-center flex-shrink-0">
                {me.rank}
              </span>
              <span className="text-[12.5px] text-slate-200 flex-1">You</span>
              <TierChip tier={me.tier} bare />
              <span className="text-[12.5px] tabular-nums text-slate-200">
                {me.score.toLocaleString()}
              </span>
            </div>
            {me.behind_next != null && (
              <div className="text-[10.5px] text-slate-500 mt-0.5 pl-[30px]">
                {me.behind_next.toLocaleString()} behind {ordinal(me.next_rank!)}
              </div>
            )}
          </div>
        )}

        {/* Tier and badges share one block: as two stacked rows with their own
            borders they cost ~14px more, which was enough to push the
            operations manager's Home past the one-screen budget the moment
            that role became rankable. */}
        {me && (
          <div className="border-t border-white/10 px-4 py-2 space-y-1.5">
            <TierProgress
              tier={me.tier}
              score={me.score}
              steps={data.tiers}
              toNext={me.to_next_tier}
              nextTier={me.next_tier}
              compact
            />
            {badgeKinds.length > 0 && (
              <div className="flex items-center gap-1.5">
                {badgeKinds.slice(0, 7).map(([kind, n]) => (
                  <span
                    key={kind}
                    className="text-[13px] leading-none"
                    title={`${BADGE_META[kind]?.label ?? kind}${n > 1 ? ` \u00d7${n}` : ""}`}
                  >
                    {BADGE_META[kind]?.icon ?? "\ud83c\udf96"}
                  </span>
                ))}
                <span className="ml-auto text-[10.5px] text-slate-500">
                  {best
                    ? `Best \u00b7 #${best.rank} in ${best.label}`
                    : `${badgeKinds.length} badge${badgeKinds.length === 1 ? "" : "s"}`}
                </span>
              </div>
            )}
          </div>
        )}

      </Link>
    </Panel>
  );
}

function ordinal(n: number): string {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}
